// src/components/Hero.js
import React from "react";
import styled from "styled-components";
import { FaArrowRight, FaCheckCircle, FaGlobe } from "react-icons/fa";
import theme from "../styles/theme";
import { withPublicUrl } from "../config";

const HeroSection = styled.section`
  min-height: 100vh;
  background: ${theme.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 ${theme.spacing[4]};
  margin-top: -1px;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(
      circle at 20% 80%,
      rgba(37, 99, 235, 0.3) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(29, 78, 216, 0.4) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 40% 40%,
      rgba(30, 64, 175, 0.2) 0%,
      transparent 50%
    );
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1300px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[16]};
  align-items: center;
  z-index: 2;
  position: relative;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[8]};
    text-align: center;
  }
`;

const Content = styled.div`
  color: white;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  background: ${theme.glass.background};
  backdrop-filter: ${theme.glass.backdropFilter};
  border: ${theme.glass.border};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[6]};
  transition: all ${theme.animation.normal} ease;

  &:hover {
    background: ${theme.glass.backgroundStrong};
    transform: translateY(-1px);
  }

  svg {
    color: ${theme.accent.emerald[400]};
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(${theme.typography.fontSize['3xl']}, 5vw, ${theme.typography.fontSize['5xl']});
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: ${theme.typography.lineHeight.tight};
  margin-bottom: ${theme.spacing[6]};
  background: linear-gradient(135deg, #ffffff 0%, ${theme.secondary[100]} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[8]};
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[10]};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.9);
  font-weight: ${theme.typography.fontWeight.medium};

  svg {
    color: ${theme.accent.emerald[400]};
    flex-shrink: 0;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  background: ${theme.gradients.accent};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all ${theme.animation.normal} ease;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
  }

  svg {
    transition: transform ${theme.animation.normal} ease;
  }

  &:hover svg {
    transform: translateX(2px);
  }
`;

const SecondaryButton = styled.button`
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  background: ${theme.glass.background};
  backdrop-filter: ${theme.glass.backdropFilter};
  border: ${theme.glass.border};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.animation.normal} ease;

  &:hover {
    background: ${theme.glass.backgroundStrong};
    transform: translateY(-2px);
  }
`;

const VisualSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const LogoVideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 700px;
  border-radius: ${theme.borderRadius['2xl']};
  overflow: hidden;
  box-shadow: ${theme.shadows['2xl']};
  background: ${theme.glass.background};
  backdrop-filter: ${theme.glass.backdropFilterStrong};
  border: ${theme.glass.border};
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%,
      rgba(255, 255, 255, 0.05) 100%
    );
    z-index: 2;
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 90%;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    max-width: 95%;
  }
`;

const LogoVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
  border-radius: ${theme.borderRadius.xl};
  
  &::-webkit-media-controls {
    display: none;
  }
  
  &::-webkit-media-controls-enclosure {
    display: none;
  }
`;

// Card components removed - replaced with logo video

const FloatingElement = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: float 6s ease-in-out infinite;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  &:nth-child(1) {
    top: 10%;
    left: 10%;
    animation-delay: 0s;
  }

  &:nth-child(2) {
    top: 20%;
    right: 10%;
    animation-delay: 2s;
  }

  &:nth-child(3) {
    bottom: 20%;
    left: 15%;
    animation-delay: 4s;
  }
`;

const Hero = () => {
  return (
    <HeroSection id="home">
      <BackgroundPattern />
      <FloatingElement />
      <FloatingElement />
      <FloatingElement />

      <Container>
        <Content>
          <Badge>
            <FaGlobe />
            500+ successful audits. Let’s make yours next
          </Badge>

          <HeroTitle>
            ISO & AISC Accreditation Made Easy—for Labs, Inspectors, & Fabricators Worldwide
          </HeroTitle>

          <HeroSubtitle>
            Navigating ISO and AISC accreditation doesn't have to be overwhelming. At ISO Audit Pro, we simplify the process for laboratories, inspection bodies, and structural fabricators worldwide. With over 30 years of hands-on experience and 500+ successful audits, we deliver expert guidance, tailored strategies, and global support—so you can achieve compliance faster, with confidence and clarity.
          </HeroSubtitle>

          <FeaturesList>
            <FeatureItem>
              <FaCheckCircle />
              ISO 17025 Testing Laboratory Accreditation
            </FeatureItem>
            <FeatureItem>
              <FaCheckCircle />
              ISO 17020 Inspection Body Certification
            </FeatureItem>
            <FeatureItem>
              <FaCheckCircle />
              AISC Structural Fabricator Accreditation
            </FeatureItem>
            <FeatureItem>
              <FaGlobe />
              International Training & Implementation
            </FeatureItem>
          </FeaturesList>

          <CTAButtons>
            <PrimaryButton
              onClick={() => {
                document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Book your free Consultation <FaArrowRight />
            </PrimaryButton>
            <SecondaryButton
              onClick={() => {
                document.getElementById('services').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Explore Services
            </SecondaryButton>
          </CTAButtons>
        </Content>

        <VisualSection>
          <LogoVideoContainer>
            <LogoVideo
              autoPlay
              muted
              loop
              playsInline
              poster={withPublicUrl("/logo.png")}
            >
              <source src={withPublicUrl("/logo.mp4")} type="video/mp4" />
              <img 
                src={withPublicUrl("/logo.png")} 
                alt="ISO Audit Pro - Global ISO 17025, ISO 17020 & AISC Accreditation Consulting"
                style={{ width: '100%', height: 'auto' }}
              />
            </LogoVideo>
          </LogoVideoContainer>
          
          <div style={{ 
            marginTop: theme.spacing[6], 
            maxWidth: '100%',
            fontSize: theme.typography.fontSize.base,
            textAlign: 'center',
            color: 'white',
          }}>
            Ready to modernize your compliance system? Our experts combine ISO knowledge with smart software and AI tools to build the right workflow for your business.
          </div>

          <Badge style={{ marginBottom: theme.spacing[8], marginTop: theme.spacing[8], color: 'white', }}>
            <FaGlobe />
            Global expertise. Local results. Wherever you operate, we've got you covered.
          </Badge>
        </VisualSection>
      </Container>
    </HeroSection>
  );
};

export default Hero;
